'use client';

import * as React from 'react';
import { Auth } from 'firebase/auth';
import { Fade, Skeleton, Typography } from '@mui/material';
import { SeriesByIdEventKeys, SeriesByIdEvents } from '../../events';
import { EventBus } from '@/shared/utils';
import { LessonProgresses } from '../../types/lessons';
import VideoPlayer from 'src/shared/components/video-player/components/video-player';
import { useSeriesByIdStore } from '../../store/series-by-id-store';
import { generateVideoMetadata } from 'src/shared/utils/generate-video-metadata';

type MuxApiResponse = { data: string | null; status: number; success: boolean };
interface MuxVideoPlayerWithTokenProps {
  auth: Auth | null;
  title: string | null;
  playbackId: string;
  lessonDocumentId: string;
  seriesDocumentId: string;
  teacherProfileDocumentId: string;
  duration: number;
  lessonProgress: LessonProgresses | null;
  eventBus: EventBus<SeriesByIdEvents>;
}

const MuxVideoPlayerWithToken: React.FC<MuxVideoPlayerWithTokenProps> = ({
  auth,
  title,
  playbackId,
  lessonDocumentId,
  teacherProfileDocumentId,
  eventBus,
  duration,
  seriesDocumentId,
  lessonProgress,
}) => {
  const [playbackToken, setPlaybackToken] = React.useState<{
    token: string | null;
    status: 'success' | 'error';
  } | null>(null);
  const playerId = 'main-player';

  React.useEffect(() => {
    if (!auth) return;
    const fetchPlaybackToken = async (id: string) => {
      try {
        // retrive player token for video mux
        const accessToken = await auth.currentUser!.getIdToken(true);
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_HOST}/api/mux-video/view?email=${auth?.currentUser?.email}&playbackId=${id}&lessonDocumentId=${lessonDocumentId}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        const dataResponse: MuxApiResponse = await res.json();
        if (dataResponse.status >= 400) {
          setPlaybackToken({ token: null, status: 'error' });
          return;
        }
        setPlaybackToken({ token: dataResponse.data, status: 'success' });
      } catch (error) {
        console.log('Error in retriving playbackIdToken:', error);
        setPlaybackToken({ token: null, status: 'error' });
        return;
      }
    };
    if (!playbackToken) fetchPlaybackToken(playbackId);
  }, [auth, playbackId, lessonDocumentId]);

  // loading state
  if (!playbackToken)
    return <Skeleton animation="wave" variant="rounded" height={500} className="rounded-xl" />;

  // error state
  if (playbackToken.status === 'error')
    return (
      <Fade in>
        <div className="h-[180px] md:h-[350px] w-full border border-[#414351] border-dotted rounded-xl flex flex-col justify-center items-center text-center relative">
          <div className="mb-6">
            <img
              src="/assets/images/checkout/cancel.svg"
              className="h-[30px] md:h-[60px] mx-auto p-2"
            />
            <Typography variant="mac-sans-h4" component="p" className="opacity-30">
              Non hai permesso di visualizzare questo contenuto
            </Typography>
          </div>
        </div>
      </Fade>
    );

  // update progress value
  const handleWatchedLesson = (
    lessonProgressDocumentId: string | null,
    lessonDocumentId: string,
    progress: number,
    lessonDuration: number,
    seriesDocumentId: string,
  ) => {
    // if the lesson has not been watched yet, create a new lesson progress
    if (!lessonProgressDocumentId) {
      eventBus.emit(SeriesByIdEventKeys.lessonProgresses.create, {
        documentId: lessonDocumentId,
        progress_status: 'in_progress',
        current_second: 0,
        seriesDocumentId,
      });
      return;
    }
    // update lesson progress
    eventBus.emit(SeriesByIdEventKeys.lessonProgresses.update, {
      documentId: lessonProgressDocumentId,
      progress_status: progress >= lessonDuration ? 'completed' : 'in_progress',
      current_second: progress,
    });
  };

  return (
    <VideoPlayer
      playerId={playerId}
      autoPlay={true}
      endScreen={true}
      isLastVideo={true}
      lessonDocumentId={lessonDocumentId}
      onProgressSave={(newProgress) => {
        const createdProgressDocumentId = useSeriesByIdStore
          .getState()
          .series?.data?.flatMap((s) => s?.lessons || [])
          ?.find((l) => l?.documentId === lessonDocumentId)?.lessonProgresses?.[0]?.documentId;
        handleWatchedLesson(
          createdProgressDocumentId || lessonProgress?.documentId || null,
          lessonDocumentId,
          newProgress.current_second,
          duration,
          seriesDocumentId,
        );
      }}
      playbackId={playbackId || ''}
      playbackToken={playbackToken.token || ''}
      videoId=""
      videoTitle=""
      muxMetadata={{
        ...generateVideoMetadata({
          serieId: seriesDocumentId,
          insegnanteId: teacherProfileDocumentId,
        }),
      }}
    />
  );
};

export { MuxVideoPlayerWithToken };
